// Message search filters, Discord's way: typed into the search box as `key:value` next to the words
// ("from:Maya has:image in:#trips beach"). The web app parses the box (parseSearch) and sends the words and the
// filters; the room (ServerRoom.search) applies them, only ever within what the person can see. A value with spaces
// is quoted: from:"Maya Lee".

/** What `has:` can ask for. */
export const SEARCH_HAS = ["link", "embed", "file", "image", "video", "gif", "sticker"] as const;
export type SearchHas = (typeof SEARCH_HAS)[number];

/** The filter keys, in the order the search box suggests them. */
export const SEARCH_KEYS = ["from", "mentions", "has", "in", "before", "after", "during", "pinned"] as const;
export type SearchKey = (typeof SEARCH_KEYS)[number];

/** How the search box describes each key in its suggestions. */
export const SEARCH_KEY_HINTS: Record<SearchKey, string> = {
  from: "From a specific person",
  mentions: "Mentions a specific person",
  has: "Has a link, embed, file, image, video, GIF or sticker",
  in: "In a specific channel",
  before: "Before a date",
  after: "After a date",
  during: "On a date",
  pinned: "Pinned messages",
};

/** The structured part of a search, sent beside the words. Every filter narrows the results (they all must hold). */
export interface SearchFilters {
  /** Written by one of these people (user ids). */
  from?: string[];
  /** @mentions one of these people (user ids). */
  mentions?: string[];
  /** Has every one of these. */
  has?: SearchHas[];
  /** In this text channel (or its threads). */
  in?: string;
  /** Sent at or after this time (ms). */
  since?: number;
  /** Sent before this time (ms). */
  until?: number;
  /** Pinned in its conversation. */
  pinned?: boolean;
}

/** At most this many people in from: or mentions: (each a bound value in the room's SQL). */
export const SEARCH_MAX_PEOPLE = 10;

/** One `key:value` in the box, as typed, with what it means (or why it doesn't). */
export interface SearchToken {
  key: SearchKey;
  /** The value as typed, without quotes. */
  value: string;
  /** Where it sits in the box (start inclusive, end exclusive), for taking it back out. */
  start: number;
  end: number;
  /** How the box shows it once understood ("From: Maya", "Has: image"); empty when it isn't. */
  label: string;
  /** Why it can't be used ("No one here is called Mya"). */
  error?: string;
}

export interface ParsedSearch {
  words: string[];
  filters: SearchFilters;
  tokens: SearchToken[];
}

export interface SearchContext {
  people: { id: string; name: string }[];
  /** Text channels. */
  channels: { id: string; name: string }[];
  /** My user id, for from:me. */
  me?: string | null;
  /** For today/yesterday (default: now). */
  now?: number;
}

const TOKEN = /(^|\s)([a-z]+):("[^"]*"?|\S*)/gi;

/** Reads the search box: its words, the filters it names, and each `key:value` (understood or not). */
export function parseSearch(q: string, ctx: SearchContext): ParsedSearch {
  const tokens: SearchToken[] = [];
  const filters: SearchFilters = {};
  let rest = "";
  let last = 0;
  for (const m of q.matchAll(TOKEN)) {
    const key = m[2]!.toLowerCase() as SearchKey;
    if (!(SEARCH_KEYS as readonly string[]).includes(key)) continue;
    const start = m.index! + m[1]!.length;
    const end = m.index! + m[0].length;
    const raw = m[3]!;
    const value = raw.startsWith('"') ? raw.slice(1, raw.endsWith('"') && raw.length > 1 ? -1 : undefined) : raw;
    rest += q.slice(last, start);
    last = end;
    tokens.push(readToken(key, value.trim(), start, end, ctx, filters));
  }
  rest += q.slice(last);
  return { words: rest.split(/\s+/).filter(Boolean), filters, tokens };
}

function readToken(key: SearchKey, value: string, start: number, end: number, ctx: SearchContext, f: SearchFilters): SearchToken {
  const t = (label: string, error?: string): SearchToken => ({ key, value, start, end, label, ...(error ? { error } : {}) });
  if (!value) return t("", `Finish ${key}: or take it out`);
  switch (key) {
    case "from":
    case "mentions": {
      const p = findPerson(value, ctx);
      if (!p) return t("", `No one here is called ${value}`);
      const list = (f[key] ??= []);
      if (!list.includes(p.id)) {
        if (list.length >= SEARCH_MAX_PEOPLE) return t("", `At most ${SEARCH_MAX_PEOPLE} people in ${key}:`);
        list.push(p.id);
      }
      return t(`${key === "from" ? "From" : "Mentions"}: ${p.name}`);
    }
    case "has": {
      const v = value.toLowerCase() === "attachment" ? "file" : value.toLowerCase();
      if (!(SEARCH_HAS as readonly string[]).includes(v)) return t("", `has: takes ${SEARCH_HAS.join(", ")}`);
      const list = (f.has ??= []);
      if (!list.includes(v as SearchHas)) list.push(v as SearchHas);
      return t(`Has: ${v}`);
    }
    case "in": {
      const name = value.replace(/^#/, "").toLowerCase();
      const c = ctx.channels.find((x) => x.name.toLowerCase() === name);
      if (!c) return t("", `No channel called #${name}`);
      if (f.in && f.in !== c.id) return t("", "One in: at a time");
      f.in = c.id;
      return t(`In: #${c.name}`);
    }
    case "before":
    case "after":
    case "during": {
      const day = parseDay(value, ctx.now ?? Date.now());
      if (day === null) return t("", `${key}: takes a date like 2026-10-31, today or yesterday`);
      const next = nextDay(day);
      if (key === "before") f.until = Math.min(f.until ?? Infinity, day);
      else if (key === "after") f.since = Math.max(f.since ?? 0, next);
      else {
        f.since = Math.max(f.since ?? 0, day);
        f.until = Math.min(f.until ?? Infinity, next);
      }
      const shown = new Date(day).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
      return t(`${key === "before" ? "Before" : key === "after" ? "After" : "On"}: ${shown}`);
    }
    case "pinned": {
      const v = value.toLowerCase();
      if (v !== "true" && v !== "yes") return t("", "pinned: takes true");
      f.pinned = true;
      return t("Pinned");
    }
  }
}

/** A person by name (any case, "@" optional, underscores for spaces), or "me". */
function findPerson(value: string, ctx: SearchContext): { id: string; name: string } | undefined {
  const v = value.replace(/^@/, "").toLowerCase();
  if (v === "me" && ctx.me) return ctx.people.find((p) => p.id === ctx.me) ?? { id: ctx.me, name: "me" };
  return ctx.people.find((p) => p.name.toLowerCase() === v) ?? ctx.people.find((p) => p.name.toLowerCase().replace(/\s+/g, "_") === v);
}

/** The start of a day in local time: YYYY-MM-DD (or YYYY/M/D), today or yesterday. Null for anything else. */
export function parseDay(value: string, now: number): number | null {
  const v = value.toLowerCase();
  if (v === "today" || v === "yesterday") {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    if (v === "yesterday") d.setDate(d.getDate() - 1);
    return d.getTime();
  }
  const m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) return null;
  return date.getTime();
}

/** The start of the next local day (not +24 h: daylight saving makes some days 23 or 25 hours). */
function nextDay(dayStart: number): number {
  const d = new Date(dayStart);
  d.setDate(d.getDate() + 1);
  return d.getTime();
}

/** Whether there's anything to search for: words, or at least one filter. */
export function searchHasQuery(p: Pick<ParsedSearch, "words" | "filters">): boolean {
  const f = p.filters;
  return p.words.length > 0 || !!(f.from?.length || f.mentions?.length || f.has?.length || f.in || f.since || f.until || f.pinned);
}

/** A value as it goes in the box: quoted when it has a space. */
export const searchValue = (v: string): string => (/\s/.test(v) ? `"${v}"` : v);

/**
 * The room's check of filters from a client: only known keys and kinds of values, people lists capped. Returns the
 * cleaned filters, or a reason to refuse them.
 */
export function cleanSearchFilters(raw: unknown): SearchFilters | string {
  if (raw === undefined || raw === null) return {};
  if (typeof raw !== "object" || Array.isArray(raw)) return "Bad search filters.";
  const r = raw as Record<string, unknown>;
  const out: SearchFilters = {};
  for (const key of ["from", "mentions"] as const) {
    const v = r[key];
    if (v === undefined) continue;
    if (!Array.isArray(v) || v.length > SEARCH_MAX_PEOPLE || !v.every((x) => typeof x === "string" && x.length > 0 && x.length <= 64)) return `Bad ${key} filter.`;
    if (v.length) out[key] = [...new Set(v as string[])];
  }
  if (r.has !== undefined) {
    if (!Array.isArray(r.has) || !r.has.every((x) => (SEARCH_HAS as readonly unknown[]).includes(x))) return "Bad has filter.";
    if (r.has.length) out.has = [...new Set(r.has as SearchHas[])];
  }
  if (r.in !== undefined) {
    if (typeof r.in !== "string" || !r.in) return "Bad in filter.";
    out.in = r.in;
  }
  for (const key of ["since", "until"] as const) {
    const v = r[key];
    if (v === undefined) continue;
    if (typeof v !== "number" || !Number.isFinite(v) || v < 0) return `Bad ${key} filter.`;
    out[key] = v;
  }
  if (r.pinned !== undefined) {
    if (typeof r.pinned !== "boolean") return "Bad pinned filter.";
    if (r.pinned) out.pinned = true;
  }
  return out;
}
